// react
import { SyntheticEvent, useContext, useEffect, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';

// context
import {
	ContextCashBenefits,
	ContextHistoricalTransactions,
	ContextNonCashBenefits,
	ContextOpEx,
	ContextPayments,
	ContextProject,
	ContextTransfers,
	ContextUserProjectPermissions,
} from '../Project.context';
import { ContextUser } from '../../../../App.context';

// mui
import { Container, Grid, Typography } from '@mui/material';

// ui
import { Tabs } from '../../../ui/Tabs/Tabs';

// pages
import { QuickActions } from './QuickActions/QuickActions';

// dialogs
import { DialogAddNonCashBenefit } from '../../../dialogs/DialogAddNonCashBenefit/DialogAddNonCashBenefit';
import { DialogAddTransaction } from '../../../dialogs/DialogAddTransaction/DialogAddTransaction';
import { DialogAddTransfer } from '../../../dialogs/DialogAddTransfer/DialogAddTransfer';
import { DialogUploadHistoricalTransactions } from '../../../dialogs/DialogUploadHistoricalTransactions/DialogUploadHistoricalTransactions';

// props
export interface TransactionsProps {
	hasQuickActions?: boolean;
	hasTabs?: boolean;
}

export const Transactions = ({ hasQuickActions = false, hasTabs = false }: TransactionsProps) => {
	// context
	const { refetchCashBenefits } = useContext(ContextCashBenefits);
	const { refetchHistoricalTransactions } = useContext(ContextHistoricalTransactions);
	const { refetchNonCashBenefits } = useContext(ContextNonCashBenefits);
	const { refetchOpEx } = useContext(ContextOpEx);
	const { refetchPayments } = useContext(ContextPayments);
	const { project } = useContext(ContextProject);
	const { refetchTransfers } = useContext(ContextTransfers);
	const { user } = useContext(ContextUser);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const location = useLocation();
	const { t } = useTranslation();

	// state
	const [searchParams, setSearchParams] = useSearchParams();
	const [tabValue, setTabValue] = useState('payments');

	// params
	const paramDialog = searchParams.get('dialog');

	// vars
	let numOfActions = 0;

	if (userProjectPermissions?.payment?.create) numOfActions++;
	if (userProjectPermissions?.cashBenefit?.create) numOfActions++;
	if (userProjectPermissions?.nonCashBenefit?.create) numOfActions++;
	if (userProjectPermissions?.opEx?.create) numOfActions++;
	if (userProjectPermissions?.transfer?.create) numOfActions++;
	if (userProjectPermissions?.document?.create) numOfActions++;

	const tabs = [];

	if (hasTabs) {
		if (userProjectPermissions?.payment?.read) {
			tabs.push({
				component: RouterLink,
				label: t('payments'),
				to: `/project/${project?.idWbg}/transactions/payments`,
				value: 'payments',
			});
		}

		if (userProjectPermissions?.cashBenefit?.read) {
			tabs.push({
				component: RouterLink,
				label: t('cashBenefits'),
				to: `/project/${project?.idWbg}/transactions/cash-benefits`,
				value: 'cashBenefits',
			});
		}

		if (userProjectPermissions?.nonCashBenefit?.read) {
			tabs.push({
				component: RouterLink,
				label: t('nonCashBenefits'),
				to: `/project/${project?.idWbg}/transactions/non-cash-benefits`,
				value: 'nonCashBenefits',
			});
		}

		if (userProjectPermissions?.opEx?.read) {
			tabs.push({
				component: RouterLink,
				label: t('operatingExpenses'),
				to: `/project/${project?.idWbg}/transactions/opex`,
				value: 'opex',
			});
		}

		if (userProjectPermissions?.transfer?.read) {
			tabs.push({
				component: RouterLink,
				label: t('transfers'),
				to: `/project/${project?.idWbg}/transactions/transfers`,
				value: 'transfers',
			});
		}

		// Show reimbursements tab to WBG users or users with reimbursement.read permission
		if (user?.isWbg || userProjectPermissions?.reimbursement?.read) {
			tabs.push({
				component: RouterLink,
				label: t('reimbursements'),
				to: `/project/${project?.idWbg}/transactions/reimbursements`,
				value: 'reimbursements',
			});
		}

		if (userProjectPermissions?.document?.read) {
			tabs.push({
				component: RouterLink,
				label: t('historicalTransactions'),
				to: `/project/${project?.idWbg}/transactions/historical`,
				value: 'historical',
			});
		}
	}

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			params.delete('dialogToEntity');
			return params;
		});
	};

	// set active tab
	useEffect(() => {
		if (hasTabs) {
			let tabValueByLocation = 'payments';

			if (location.pathname.includes('non-cash-benefits')) {
				tabValueByLocation = 'nonCashBenefits';
			} else if (location.pathname.includes('cash-benefits')) {
				tabValueByLocation = 'cashBenefits';
			} else if (location.pathname.includes('historical')) {
				tabValueByLocation = 'historical';
			} else if (location.pathname.includes('opex')) {
				tabValueByLocation = 'opex';
			} else if (location.pathname.includes('reimbursements')) {
				tabValueByLocation = 'reimbursements';
			} else if (location.pathname.includes('transfers')) {
				tabValueByLocation = 'transfers';
			}

			setTabValue(tabValueByLocation);
		}
	}, [hasTabs, location]);

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Container maxWidth={false}>
						<Typography variant="h2">{t('transactions')}</Typography>
					</Container>
				</Grid>
				{hasQuickActions && numOfActions >= 3 && (
					<Grid item xs={12}>
						<QuickActions />
					</Grid>
				)}
				{hasTabs && (
					<Grid item xs={12}>
						<Tabs hasBorder={false} onChange={handleChangeTab} tabs={tabs} value={tabValue} />
					</Grid>
				)}
				<Grid item xs={12}>
					<Outlet />
				</Grid>
			</Grid>
			{userProjectPermissions?.nonCashBenefit?.create && (
				<DialogAddNonCashBenefit
					isOpen={paramDialog === 'recordNonCashBenefit'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchNonCashBenefits();
					}}
				/>
			)}
			{userProjectPermissions?.payment?.create && (
				<DialogAddTransaction
					isOpen={paramDialog === 'recordPayment'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchPayments();
					}}
					type="PAYMENT"
				/>
			)}
			{userProjectPermissions?.cashBenefit?.create && (
				<DialogAddTransaction
					isOpen={paramDialog === 'recordCashBenefit'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchCashBenefits();
					}}
					type="CASH_BENEFIT"
				/>
			)}
			{userProjectPermissions?.opEx?.create && (
				<DialogAddTransaction
					isOpen={paramDialog === 'recordOpEx'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchOpEx();
					}}
					type="OPEX"
				/>
			)}
			{userProjectPermissions?.transfer?.create && (
				<DialogAddTransfer
					isOpen={paramDialog === 'recordFundTransfer'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchTransfers();
					}}
				/>
			)}
			{userProjectPermissions?.document?.create && (
				<DialogUploadHistoricalTransactions
					isOpen={paramDialog === 'uploadHistoricalTransactions'}
					onClose={handleCloseDialog}
					onSuccess={() => {
						refetchHistoricalTransactions();
					}}
				/>
			)}
		</>
	);
};
